/**
 * MediaDeviceInfoWithState - Extended device info with connection state tracking
 *
 * Wraps the browser's MediaDeviceInfo with additional state for:
 * - Tracking connection status (connected/disconnected)
 * - Remembering user's device selection across sessions
 * - Associating output devices with specific panels
 *
 * ## Disconnected Device Visibility
 *
 * When a device is disconnected, it's kept in the device list with `isActive = false`.
 * This allows the UI to:
 * - Show "(inactive)" prefix in the device label
 * - Apply "device-selected-disconnected" CSS class for visual styling
 * - Automatically reconnect when the device is plugged back in
 *
 * Devices are persisted in localStorage and kept for up to 10 days after disconnection.
 *
 * @see MediaDevicesUtils - manages device enumeration and persistence
 * @see DeviceSelector - UI component that displays connection status
 */
class MediaDeviceInfoWithState implements MediaDeviceInfo {
    /** Whether the device is currently connected. False = disconnected but remembered */
    isActive: boolean = true;
    /** Timestamp of last time this device was seen connected (used for 10-day expiry) */
    private _lastSeen: number = Date.now();
    /** Whether this device should be auto-selected on page load */
    preselected: boolean = false;
    /** For output devices: which panel this device is assigned to */
    lastPanelId: string = '';
    /** The underlying browser MediaDeviceInfo */
    mediaDeviceInfo: MediaDeviceInfo;

    constructor(info: MediaDeviceInfo) {
        this.mediaDeviceInfo = info;
        this.isActive = true;
    }

    get deviceId(): string {
        return this.mediaDeviceInfo.deviceId;
    }

    get groupId(): string {
        return this.mediaDeviceInfo.groupId;
    }

    get kind(): MediaDeviceKind {
        return this.mediaDeviceInfo.kind;
    }

    get label(): string {
        return this.mediaDeviceInfo.label;
    }

    get lastSeen(): number {
        return this._lastSeen;
    }

    set lastSeen(value: number) {
        this._lastSeen = value;
    }

    toJSON(): any {
        return {
            // Include all properties from MediaDeviceInfoWithState
            isActive: this.isActive,
            lastSeen: this._lastSeen,
            preselected: this.preselected,
            lastPanelId: this.lastPanelId,

            // Include all properties from the underlying MediaDeviceInfo
            deviceId: this.deviceId,
            groupId: this.groupId,
            kind: this.kind,
            label: this.label
        };
    }
}

export default MediaDeviceInfoWithState;
